import { useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { formatDate, formatDateValue, formatDateValueForInput, formatDateYYYYMMDD } from '../common/UtilityFunctions';
import useGetLossDraftRepairList from '../common/hooks/useGetLossDraftRepairList';
import { LossDraftRepair } from '../types/LossDraftRepairTypes';
import styles from '../css/LossDraftRepair.module.css';

interface InsuranceClaimListProps {
  loanNumber: string;
  onSelectClaim: (claimId: string) => void;
}

interface NewClaim {
  dateInitialDetailsAdded: string;
  claimNo: string;
  insuranceCompanyName: string;
  dateOfLoss: string;
  claimRCV: string;
}

const InsuranceClaimList = ({ loanNumber, onSelectClaim }: InsuranceClaimListProps) => {
  const { lossDraftRepairs, loadingLossDraftRepairs, lossDraftRepairListError, createLossDraftRepair } =
    useGetLossDraftRepairList(loanNumber);
  const [newClaim, setNewClaim] = useState<NewClaim | null>(null);
  const [newClaimErrors, setNewClaimErrors] = useState<{
    claimNo?: string;
    insuranceCompanyName?: string;
  }>({});

  const sortedLossDraftRepairs = [...lossDraftRepairs].sort((a, b) =>
    formatDateValueForInput(b.dateInitialDetailsAdded).localeCompare(
      formatDateValueForInput(a.dateInitialDetailsAdded),
    ),
  );

  const handleAddClaim = () => {
    if (newClaim) {
      return;
    }

    setNewClaim({
      dateInitialDetailsAdded: formatDate(new Date()),
      claimNo: '',
      insuranceCompanyName: '',
      dateOfLoss: '',
      claimRCV: '',
    });

    setNewClaimErrors({});
  };

  const handleNewClaimChange = (field: keyof NewClaim, value: string) => {
    setNewClaim((previous) => {
      if (!previous) {
        return previous;
      }

      return {
        ...previous,
        [field]: value,
      };
    });

    if ((field === 'claimNo' || field === 'insuranceCompanyName') && value.trim()) {
      setNewClaimErrors((current) => {
        const updatedErrors = { ...current };
        delete updatedErrors[field];
        return updatedErrors;
      });
    }
  };

  const handleCancelNewClaim = () => {
    setNewClaim(null);
    setNewClaimErrors({});
  };

  const handleSaveNewClaim = async () => {
    if (!newClaim) {
      return;
    }

    const errors: {
      claimNo?: string;
      insuranceCompanyName?: string;
    } = {};

    if (!newClaim.claimNo.trim()) {
      errors.claimNo = 'Claim Number Required';
    }

    if (!newClaim.insuranceCompanyName.trim()) {
      errors.insuranceCompanyName = 'Insurance Company Name Required';
    }

    if (Object.keys(errors).length > 0) {
      setNewClaimErrors(errors);
      return;
    }

    const claimNumber = newClaim.claimNo.trim();

    const success = await createLossDraftRepair({
      loanNo: loanNumber,
      claimId: claimNumber,
      claimNo: claimNumber,
      insuranceCompanyName: newClaim.insuranceCompanyName.trim(),
      dateInitialDetailsAdded: Math.floor(Date.now() / 1000),
      dateOfLoss: newClaim.dateOfLoss ? Math.floor(new Date(`${newClaim.dateOfLoss}T00:00:00`).getTime() / 1000) : null,
      claimRCV: newClaim.claimRCV ? Number(newClaim.claimRCV.replace(/[$,]/g, '')) : null,
    });

    if (success) {
      setNewClaim(null);
      setNewClaimErrors({});
    }
  };

  if (loadingLossDraftRepairs) {
    return (
      <div className={styles.page}>
        <div className={styles.message}>Loading insurance claims...</div>
      </div>
    );
  }

  if (lossDraftRepairListError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorMessage}>{lossDraftRepairListError}</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.claimListContainer}>
        <div className={styles.claimListHeader}>
          <span>Insurance Claim Details</span>

          <button type='button' className={styles.addClaimButton} onClick={handleAddClaim} disabled={newClaim !== null}>
            + Add another claim
          </button>
        </div>

        <div className={styles.tableWrapper}>
          <table className={`${styles.detailTable} ${styles.claimTable}`}>
            <thead>
              <tr>
                <th>Date Initial Details Added</th>
                <th>Claim #</th>
                <th>Insurance Company Name</th>
                <th>Date of Loss</th>
                <th>Claim RCV</th>
              </tr>
            </thead>

            <tbody>
              {lossDraftRepairs.length === 0 && !newClaim ? (
                <tr>
                  <td className={styles.emptyTable} colSpan={5}>
                    No insurance claims found for this loan.
                  </td>
                </tr>
              ) : (
                sortedLossDraftRepairs.map((claim: LossDraftRepair) => (
                  <tr key={claim.claimId}>
                    <td>
                      <div className={styles.claimValueBox}>
                        {formatDateValue(claim.dateInitialDetailsAdded) || (
                          <span className={styles.placeholderValue}>MM/DD/YYYY</span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className={styles.claimValueBox}>
                        {claim.claimNo ? (
                          <button
                            type='button'
                            className={styles.claimNumberButton}
                            onClick={() => onSelectClaim(claim.claimId)}
                          >
                            {claim.claimNo}
                          </button>
                        ) : (
                          <span className={styles.placeholderValue}>************</span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className={styles.claimValueBox}>
                        {claim.insuranceCompanyName || (
                          <span className={styles.placeholderValue}>Insurance Company Name</span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className={styles.claimValueBox}>
                        {formatDateValue(claim.dateOfLoss) || (
                          <span className={styles.placeholderValue}>MM/DD/YYYY</span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className={styles.claimValueBox}>
                        {claim.claimRCV !== null && claim.claimRCV !== undefined && claim.claimRCV !== 0 ? (
                          <NumericFormat
                            value={claim.claimRCV}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator
                            prefix='$'
                            displayType='text'
                          />
                        ) : (
                          <span className={styles.placeholderValue}>$00,000.00</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {newClaim && (
                <tr className={styles.newClaimRow}>
                  <td>
                    <div className={styles.claimValueBox}>{newClaim.dateInitialDetailsAdded}</div>
                  </td>

                  <td className={styles.newClaimNumberCell}>
                    {!newClaim.claimNo && <span className={styles.newClaimNumberPlaceholder}>************</span>}
                    <input
                      className={`${styles.newClaimInput} ${styles.newClaimNumberInput}`}
                      type='text'
                      maxLength={12}
                      value={newClaim.claimNo}
                      onChange={(e) => handleNewClaimChange('claimNo', e.target.value.toUpperCase())}
                    />

                    {newClaimErrors.claimNo && (
                      <div className={styles.claimValidationError}>{newClaimErrors.claimNo}</div>
                    )}
                  </td>

                  <td>
                    <input
                      className={styles.newClaimInput}
                      type='text'
                      maxLength={20}
                      value={newClaim.insuranceCompanyName}
                      placeholder='Insurance Company Name'
                      onChange={(e) => handleNewClaimChange('insuranceCompanyName', e.target.value)}
                    />

                    {newClaimErrors.insuranceCompanyName && (
                      <div className={styles.claimValidationError}>{newClaimErrors.insuranceCompanyName}</div>
                    )}
                  </td>

                  <td>
                    <input
                      className={`${styles.newClaimInput} ${!newClaim.dateOfLoss ? styles.emptyDateInput : ''}`}
                      type='date'
                      max={formatDateYYYYMMDD(new Date())}
                      value={newClaim.dateOfLoss}
                      onChange={(e) => handleNewClaimChange('dateOfLoss', e.target.value)}
                    />
                  </td>

                  <td>
                    <NumericFormat
                      className={styles.newClaimInput}
                      value={newClaim.claimRCV}
                      placeholder='$00,000.00'
                      decimalScale={2}
                      fixedDecimalScale
                      thousandSeparator
                      prefix='$'
                      allowNegative={false}
                      isAllowed={(values) => values.floatValue === undefined || values.floatValue <= 999999999}
                      onValueChange={(values) => handleNewClaimChange('claimRCV', values.value)}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {newClaim && (
            <div className={styles.claimActions}>
              <button type='button' className={styles.secondaryButton} onClick={handleCancelNewClaim}>
                Cancel
              </button>

              <button type='button' className={styles.secondaryButton} onClick={handleSaveNewClaim}>
                Save
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsuranceClaimList;
